import { getAccessToken } from '@services/token';
import { getApiBase } from '@services/apiBase';
import { LocalStorageEncryptionService, EncryptedData } from './localStorageEncryption';
import { getBrowserId } from './browser';
import {
  getUserEncryptionKey,
  getUploadDetails as apiGetUploadDetails,
  updateUpload as apiUpdateUpload,
  deleteUpload as apiDeleteUpload,
  deleteFile as apiDeleteFile,
  updateMetadata as apiUpdateMetadata,
  getFilesDownloadInfo as apiGetFilesDownloadInfo,
  updateFileDownloadInfo as apiUpdateFileDownloadInfo,
  UploadInformation,
  UploadFileInfo,
  UpdateUploadRequest,
  DeleteUploadRequest,
  GetUploadDetailsRequest,
  FileDownloadInfo,
  UpdateFileDownloadInfoParams
} from './api';

export type {
  UploadInformation,
  UploadFileInfo,
  UpdateUploadRequest,
  DeleteUploadRequest,
  GetUploadDetailsRequest,
  FileDownloadInfo,
  UpdateFileDownloadInfoParams
};

interface StoredUploadData {
  uploads: UploadInformation[];
  lastCleanup: string;
}

const STORAGE_KEY = 'encrypted_uploads_data';

export class UploadManagementService {
  private static encryptionKey: string | null = null;
  private static cleanupInterval: NodeJS.Timeout | null = null;
  private static isInitialized = false;

  private static async migrateFallbackUploads(): Promise<void> {
    try {
      const browserId = getBrowserId();
      const fallbackKey = `anonymous_uploads_${browserId}`;
      
      const existingData = localStorage.getItem(fallbackKey);
      if (!existingData) {
        return;
      }
      
      const fallbackUploads: UploadInformation[] = JSON.parse(existingData);
      
      if (fallbackUploads.length === 0) {
        localStorage.removeItem(fallbackKey);
        return;
      }
      
      // Get current encrypted data
      const currentData = await this.getStoredData();
      
      // Add fallback uploads to encrypted storage
      currentData.uploads.push(...fallbackUploads);
      
      // Remove expired uploads
      currentData.uploads = currentData.uploads.filter(upload => !this.isExpired(upload));
      
      // Save to encrypted storage
      await this.saveStoredData(currentData);
      
      // Remove fallback data
      localStorage.removeItem(fallbackKey);
    } catch (error) {
      console.error('Failed to migrate fallback uploads:', error);
    }
  }

  static async initialize(userId?: string, browserId?: string): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Add auth token if available (for authenticated users)
      const token = getAccessToken();
      if (!token && !browserId) {
        // For anonymous users, browserId is required
        throw new Error('Either auth token or browserId must be provided');
      }
      
      const data = await getUserEncryptionKey(browserId);
      
      // Handle case where anonymous user doesn't exist yet (404 returns null)
      if (!data || !data.encryptionKey) {
        this.encryptionKey = null;
        this.startCleanupTimer();
        setTimeout(() => this.checkAndCleanupIfNeeded(), 10000);
        this.isInitialized = true;
        return;
      }
      
      this.encryptionKey = data.encryptionKey;

      // Start cleanup timer
      this.startCleanupTimer();
      
      // Check if cleanup is needed after 10 seconds
      setTimeout(() => this.checkAndCleanupIfNeeded(), 10000);
      
      // Migrate any fallback uploads for anonymous users
      if (browserId) {
        await this.migrateFallbackUploads();
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize upload management service:', error);
      throw error;
    }
  }

  private static async saveUploadForNewAnonymousUser(uploadInfo: UploadInformation): Promise<void> {
    try {
      // For new anonymous users, use a browser-specific storage key without encryption
      const browserId = getBrowserId();
      const fallbackKey = `anonymous_uploads_${browserId}`;
      
      // Get existing fallback data
      const existingData = localStorage.getItem(fallbackKey);
      let uploads: UploadInformation[] = [];
      
      if (existingData) {
        try {
          uploads = JSON.parse(existingData);
        } catch (error) {
          console.error('Failed to parse existing fallback data:', error);
          uploads = [];
        }
      }
      
      // Add new upload
      uploads.push(uploadInfo);
      
      // Remove expired uploads
      uploads = uploads.filter(upload => !this.isExpired(upload));
      
      // Save to localStorage (unencrypted for fallback scenario)
      localStorage.setItem(fallbackKey, JSON.stringify(uploads));
    } catch (error) {
      console.error('Failed to save upload for new anonymous user:', error);
      throw error;
    }
  }

  static async saveUpload(uploadInfo: UploadInformation): Promise<void> {
    // Auto-initialize if not already initialized
    if (!this.isInitialized) {
      try {
        const token = getAccessToken();
        if (token) {
          // Authenticated user
          await this.initialize(undefined, undefined);
        } else {
          // Anonymous user - use browser ID
          await this.initialize(undefined, getBrowserId());
        }
      } catch (error) {
        console.error('Failed to auto-initialize upload management service:', error);
        // For anonymous users who don't exist yet, use browser-specific fallback
        if (!getAccessToken()) {
          await this.saveUploadForNewAnonymousUser(uploadInfo);
          return;
        }
      }
    }

    if (!this.encryptionKey) {
      // Service not initialized or anonymous user doesn't exist
      // For anonymous users who don't exist yet, use browser-specific fallback
      if (!getAccessToken()) {
        await this.saveUploadForNewAnonymousUser(uploadInfo);
        return;
      }
    }

    try {
      const currentData = await this.getStoredData();
      currentData.uploads.push(uploadInfo);
      
      // Remove expired uploads
      currentData.uploads = currentData.uploads.filter(upload => !this.isExpired(upload));
      
      await this.saveStoredData(currentData);
    } catch (error) {
      console.error('Failed to save upload:', error);
      throw error;
    }
  }

  private static async getFallbackUploads(): Promise<UploadInformation[]> {
    try {
      const browserId = getBrowserId();
      const fallbackKey = `anonymous_uploads_${browserId}`;
      
      const existingData = localStorage.getItem(fallbackKey);
      if (!existingData) {
        return [];
      }
      
      const uploads: UploadInformation[] = JSON.parse(existingData);
      
      // Filter out expired uploads
      const validUploads = uploads.filter(upload => !this.isExpired(upload));
      
      // Sort by creation date descending (newest first)
      validUploads.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      // Update stored data if we removed expired uploads
      if (validUploads.length !== uploads.length) {
        localStorage.setItem(fallbackKey, JSON.stringify(validUploads));
      }
      
      return validUploads;
    } catch (error) {
      console.error('Failed to load fallback uploads:', error);
      return [];
    }
  }

  static async getUploads(): Promise<UploadInformation[]> {
    if (!this.encryptionKey) {
      // Service not initialized or anonymous user doesn't exist
      // For anonymous users, check for fallback data
      if (!getAccessToken()) {
        return await this.getFallbackUploads();
      }
      
      return [];
    }

    try {
      const data = await this.getStoredData();
      
      // Filter out expired uploads
      const validUploads = data.uploads.filter(upload => !this.isExpired(upload));
      
      // Sort by creation date descending (newest first)
      validUploads.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      // Update stored data if we removed expired uploads
      if (validUploads.length !== data.uploads.length) {
        data.uploads = validUploads;
        await this.saveStoredData(data);
      }
      
      return validUploads;
    } catch (error) {
      console.error('Failed to get uploads:', error);
      return [];
    }
  }

  static async getUploadDetails(uploadId: string, uploadAdminPassword: string): Promise<UploadInformation> {
    const storedUpload = await this.getStoredUpload(uploadId);

    const data = await apiGetUploadDetails(uploadId, uploadAdminPassword);

    if (storedUpload?.shareableLink?.includes('#')) {
      data.shareableLink = storedUpload.shareableLink;
    }

    try {
      const filesDownloadInfo = await this.getFilesDownloadInfo(uploadId, uploadAdminPassword);
      const infoMap = new Map(filesDownloadInfo.map((info) => [info.fileId, info]));

      data.files = (data.files ?? []).map((file: UploadFileInfo, index: number) => {
        const originalFile = storedUpload?.files[index];
        const resolvedId = file.id ?? originalFile?.id ?? '';
        const matchedInfo = resolvedId ? infoMap.get(resolvedId) : undefined;

        return {
          ...file,
          id: resolvedId,
          name: originalFile?.name ?? file.name,
          size: originalFile?.size ?? file.size,
          downloadsCount: matchedInfo?.downloadCount ?? file.downloadsCount,
          maxDownloadsPerFile: matchedInfo?.maxDownloads ?? file.maxDownloadsPerFile
        };
      });
    } catch (error) {
      console.error('Failed to load files download info:', error);
    }

    return data;
  }

  static async updateUpload(request: UpdateUploadRequest): Promise<UploadInformation> {
    const storedUpload = await this.getStoredUpload(request.uploadId);

    const data = await apiUpdateUpload(request);
      
    if (storedUpload?.shareableLink?.includes('#')) {
      data.shareableLink = storedUpload.shareableLink;
    }

    // Update local storage
    await this.updateLocalUpload(data);
    
    return data;
  }

  static async deleteUpload(uploadId: string, uploadAdminPassword: string): Promise<void> {
    try {
      await apiDeleteUpload(uploadId, uploadAdminPassword);
    } catch (error: any) {
      if (error instanceof Error && error.message.includes('Upload not found')) {
        await this.removeLocalUpload(uploadId);
        throw error;
      }
      throw error;
    }

    // Remove from local storage
    await this.removeLocalUpload(uploadId);
  }

  static async deleteFile(uploadId: string, fileId: string, uploadAdminPassword: string, secret?: string): Promise<void> {
    await apiDeleteFile(uploadId, fileId, uploadAdminPassword);

    // Update local storage to remove the file
    await this.removeLocalFile(uploadId, fileId);

    // If secret is provided, re-encrypt and upload updated metadata for download page
    if (secret) {
      await this.updateMetadataAfterFileDeletion(uploadId, fileId, uploadAdminPassword, secret);
    }
  }

  private static async updateMetadataAfterFileDeletion(
    uploadId: string,
    deletedFileId: string,
    uploadAdminPassword: string,
    secret: string
  ): Promise<void> {
    try {
      // Get stored upload to access current metadata
      const storedUpload = await this.getStoredUpload(uploadId);
      if (!storedUpload) {
        return;
      }

      // Create updated metadata with file removed
      const updatedFiles = storedUpload.files.filter(f => f.id !== deletedFileId);
      
      if (updatedFiles.length === 0) {
        // All files deleted, no need to update metadata (upload will be deleted)
        return;
      }

      const { encryptMetadata } = await import('@services/crypto');
      const updatedMetadata = {
        files: updatedFiles.map(f => ({
          id: f.id,
          name: f.name,
          size: f.size,
          type: f.contentType,
          lastModified: 0
        })),
        createdAt: storedUpload.createdAt
      };

      // Re-encrypt metadata with the secret
      const encryptedMetadata = await encryptMetadata(updatedMetadata, secret, uploadId);

      // Upload re-encrypted metadata to backend
      await apiUpdateMetadata(uploadId, uploadAdminPassword, encryptedMetadata);
    } catch (error) {
      console.error('Failed to update metadata after file deletion:', error);
      // Don't throw - the file was deleted successfully, this is just a metadata sync issue
    }
  }

  static async updateMetadata(uploadId: string, uploadAdminPassword: string, metadataCiphertext: string): Promise<void> {
    await apiUpdateMetadata(uploadId, uploadAdminPassword, metadataCiphertext);
  }

  static async getFilesDownloadInfo(uploadId: string, uploadAdminPassword: string): Promise<FileDownloadInfo[]> {
    return await apiGetFilesDownloadInfo(uploadId, uploadAdminPassword);
  }

  static async updateFileDownloadInfo(params: UpdateFileDownloadInfoParams): Promise<FileDownloadInfo> {
    return await apiUpdateFileDownloadInfo(params);
  }

// ... (rest of the code remains the same)
  private static async getStoredUpload(uploadId: string): Promise<UploadInformation | undefined> {
    const data = await this.getStoredData();
    return data.uploads.find((upload) => upload.uploadId === uploadId);
  }

  private static async getStoredData(): Promise<StoredUploadData> {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return { uploads: [], lastCleanup: new Date().toISOString() };
      }

      const encryptedData: EncryptedData = JSON.parse(stored);
      const decrypted = await LocalStorageEncryptionService.decrypt(encryptedData, this.encryptionKey!);
      return JSON.parse(decrypted);
    } catch (error) {
      console.error('Failed to get stored data:', error);
      // Clear corrupted data to prevent repeated decryption attempts
      localStorage.removeItem(STORAGE_KEY);
      return { uploads: [], lastCleanup: new Date().toISOString() };
    }
  }

  private static async saveStoredData(data: StoredUploadData): Promise<void> {
    if (!this.encryptionKey) {
      // Service not initialized or anonymous user doesn't exist
      return;
    }

    try {
      const jsonData = JSON.stringify(data);
      const encrypted = await LocalStorageEncryptionService.encrypt(jsonData, this.encryptionKey!);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(encrypted));
    } catch (error) {
      console.error('Failed to save stored data:', error);
      throw error;
    }
  }

  private static async updateLocalUpload(updatedUpload: UploadInformation): Promise<void> {
    const data = await this.getStoredData();
    const index = data.uploads.findIndex(upload => upload.uploadId === updatedUpload.uploadId);
    
    if (index !== -1) {
      // Preserve file names and other local data, only update changed fields
      const existingUpload = data.uploads[index];
      data.uploads[index] = {
        ...existingUpload,
        emailNotification: updatedUpload.emailNotification,
        maxDownloads: updatedUpload.maxDownloads,
        isPasswordProtected: updatedUpload.isPasswordProtected,
        expiryDate: updatedUpload.expiryDate,
        brandingProfileId: updatedUpload.brandingProfileId
      };
      await this.saveStoredData(data);
    }
  }

  private static async removeLocalUpload(uploadId: string): Promise<void> {
    const data = await this.getStoredData();
    data.uploads = data.uploads.filter(upload => upload.uploadId !== uploadId);
    await this.saveStoredData(data);
  }

  private static async removeLocalFile(uploadId: string, fileId: string): Promise<void> {
    const data = await this.getStoredData();
    const uploadIndex = data.uploads.findIndex(upload => upload.uploadId === uploadId);
    
    if (uploadIndex !== -1) {
      const upload = data.uploads[uploadIndex];
      upload.files = upload.files.filter(file => file.id !== fileId);
      await this.saveStoredData(data);
    }
  }

  private static isExpired(upload: UploadInformation): boolean {
    if (!upload.expiryDate) return false;
    return new Date(upload.expiryDate) < new Date();
  }

  private static startCleanupTimer(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    this.cleanupInterval = setInterval(async () => {
      await this.checkAndCleanupIfNeeded();
    }, 5 * 60 * 1000); // 5 minutes
  }

  private static async checkAndCleanupIfNeeded(): Promise<void> {
    try {
      const data = await this.getStoredData();
      const now = new Date();
      const lastCleanup = new Date(data.lastCleanup);
      
      // Check if 5 minutes have passed since last cleanup
      if (now.getTime() - lastCleanup.getTime() >= 5 * 60 * 1000) {
        const validUploads = data.uploads.filter(upload => !this.isExpired(upload));
        
        if (validUploads.length !== data.uploads.length) {
          data.uploads = validUploads;
          data.lastCleanup = now.toISOString();
          await this.saveStoredData(data);
        }
      }
    } catch (error) {
      console.error('Failed to check and cleanup:', error);
    }
  }

  static cleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }
}

